import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useField, UseFieldConfig } from 'react-final-form';

import Civilities from 'constants/civilities';
import { required, validateEmail, validateRequiredEmail } from 'utils/formUtils';

import { isEqualPhoneNumber, PhoneNumber } from 'modules/selfServices/types/SelfService';
import { setIsAdditionalCustomerDriving } from 'modules/selfServices/actions';
import { getIsAdditionalCustomerDriving, getSelfServiceStatus, isAdditionalCustomerQuestionEnabled } from 'modules/selfServices/selectors';
import SelfServiceStatus from 'modules/selfServices/types/SelfServiceStatus';
import { booleanToYesNoChoice, YesNoChoice, yesNoChoiceToBoolean } from 'types/Question';

import FormInput from 'components/ui/form/FormInput';
import PhoneNumberInput from 'components/ui/PhoneNumberInput';
import YesNoCardActions from 'components/ui/Card/YesNoCardActions';

import { useDispatch, useSelector } from 'hooks';
import LanguageInput from './LanguageInput';
import CivilityInput from './CivilityInput';
import { CustomerInfo } from 'modules/selfServices/types/CustomerInfo';
import { getIn } from 'final-form';
import MandatorySymbol from 'components/ui/MandatorySymbol';

const fieldConfig: UseFieldConfig<string> = { subscription: { value: true } };

export interface ContactFormProps {
  onError?: (hasError: boolean) => void;
  isPhoneNumberOrEmailMandatory?: boolean;
  isAdditionalCustomer?: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({ onError, isPhoneNumberOrEmailMandatory, isAdditionalCustomer }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selfServiceStatus = useSelector(getSelfServiceStatus);
  const additionalCustomerQuestionEnabled = useSelector(isAdditionalCustomerQuestionEnabled);
  const isAdditionalCustomerDriving = useSelector(getIsAdditionalCustomerDriving);

  const isAnswered = selfServiceStatus === SelfServiceStatus.ANSWERED;
  const showAdditionalCustomerQuestion = !isAdditionalCustomer && additionalCustomerQuestionEnabled;

  const { value: civility } = useField('civility', fieldConfig).input;
  const validatePhoneNumberInput = React.useCallback((value: PhoneNumber, allValues: CustomerInfo) => {
    if (!isAdditionalCustomer) {
      const email = getIn(allValues, 'email');
      return isPhoneNumberOrEmailMandatory && !email?.length ? required(value?.local) : undefined;
    }
    return undefined;
  }, [isAdditionalCustomer, isPhoneNumberOrEmailMandatory]);

  const phoneNumberInput = useField<PhoneNumber>('phoneNumber', {
    subscription: { value: true },
    isEqual: isEqualPhoneNumber,
    validate: validatePhoneNumberInput,
  }).input;
  const { value: email } = useField('email', fieldConfig).input;
  const hasPhoneNumber = phoneNumberInput.value?.local?.trim().length > 0;
  const hasEmail = email?.trim().length > 0;
  const emailIsRequired = isAdditionalCustomer || (isPhoneNumberOrEmailMandatory && !hasPhoneNumber);
  const phoneNumberIsRequired = !isAdditionalCustomer && isPhoneNumberOrEmailMandatory && !hasEmail;

  const isCompany = civility === Civilities.COMPANY;
  const isOtherCivility = civility === Civilities.NONE;

  const handleDriverSameAsCustomerChange = React.useCallback((value: YesNoChoice) => {
    dispatch(setIsAdditionalCustomerDriving(!yesNoChoiceToBoolean(value)));
  }, [dispatch]);

  const onPhoneNumberError = React.useCallback((error: boolean) => {
    onError?.(error);
  }, [onError]);

  const validateEmailInput = React.useCallback((email: string, allValues: CustomerInfo) => {
    if (isAdditionalCustomer) {
      return validateRequiredEmail(email);
    }
    const phoneNumberLocal = getIn(allValues, 'phoneNumber.local');
    return validateEmail(email) || isPhoneNumberOrEmailMandatory && (phoneNumberLocal?.length > 0 ? undefined : required(email));
  }, [isAdditionalCustomer, isPhoneNumberOrEmailMandatory]);

  return (
    <div className="flex flex-col gap-4 mb-4">
      <LanguageInput />
      <CivilityInput />
      {isCompany && (
        <FormInput
          label={intl.formatMessage({ id: 'customerInfo.companyName', defaultMessage: 'Company name' })}
          name="companyName"
          required
          validate={required}
          data-testid="companyName-input"
        />
      )}
      {!isCompany && (
        <>
          <FormInput
            label={intl.formatMessage({ id: 'customerInfo.firstName', defaultMessage: 'First name' })}
            name="firstName"
            data-testid="firstName-input"
            autoComplete="given-name"
          />
          <FormInput
            key={`${civility}_lastName`}
            label={intl.formatMessage({ id: 'customerInfo.lastName', defaultMessage: 'Last name' })}
            name="lastName"
            required={!isOtherCivility}
            validate={!isOtherCivility ? required : undefined}
            data-testid="lastName-input"
            autoComplete="family-name"
          />
        </>
      )}
      <PhoneNumberInput
        onError={onPhoneNumberError}
        required={phoneNumberIsRequired}
        {...phoneNumberInput}
      />
      <FormInput
        label={intl.formatMessage({ id: 'customerInfo.email', defaultMessage: 'Email' })}
        name="email"
        validate={validateEmailInput}
        required={emailIsRequired}
        data-testid="email-input"
        type="email"
        inputMode="email"
        autoComplete="email"
      />
      {showAdditionalCustomerQuestion && (
        <div className="group bg-card-bg rounded-input p-4">
          <p className="text-m font-bold mb-4 text-center text-(--color-neutral-highest)">
            <MandatorySymbol className="mr-1" />
            <FormattedMessage
              id="customerInfo.isAdditionalCustomerDriving.label"
              defaultMessage="Are you the one dropping off and picking up the car at the dealership?"
            />
          </p>
          <YesNoCardActions
            value={isAdditionalCustomerDriving === undefined ? undefined : booleanToYesNoChoice(!isAdditionalCustomerDriving)}
            onChange={handleDriverSameAsCustomerChange}
            disabled={isAnswered}
          />
        </div>
      )}
    </div>
  );
};

export default ContactForm;
